import { InsertDriveFile, MoreVert } from "@mui/icons-material"
import { Box, IconButton, Menu, MenuItem, useTheme } from "@mui/material"
import { GridColDef } from "@mui/x-data-grid"
import { memo, useState } from "react"
import { SensorInstallInfo } from "../../../models/sensors/SensorInstallInfo"
import { TimeUtilsDateFns } from "../../../utils/TimeUtils"
import { DeleteSensorInstallMenuItem } from "./DeleteSensorInstallMenuItem"
import { EditSensorInstallMenuItem } from "./EditSensorInstallButton"
import { SensorInstallOrder } from "./SensorInstallSearchFilters"

export const sensorInstallBaseColumns: GridColDef<SensorInstallInfo>[] = [
    { field: "file_name", headerName: "File Name", flex: 2, sortable: false, minWidth: 184 },
    {
        field: SensorInstallOrder.UPLOAD_DATE,
        minWidth: 144,
        headerName: "Date Uploaded",
        sortingOrder: ["desc", "asc"],
        valueGetter: (_, row) => TimeUtilsDateFns.formatDate(new Date(row.date_uploaded)),
        flex: 1,
    },
    {
        field: SensorInstallOrder.START_DATE,
        minWidth: 120,
        headerName: "Start Date",
        sortingOrder: ["desc", "asc"],
        valueGetter: (_, row) => row.valid_start,
        flex: 1,
    },
    {
        field: "valid_end",
        minWidth: 120,
        headerName: "End Date",
        sortable: false,
        filterable: false,
        valueFormatter: (_, row) => row.valid_end ?? "Present",
        flex: 1,
    },
    {
        field: "actions",
        type: "actions",
        sortable: false,
        filterable: false,
        renderCell: (params) => <SensorInstallActions sensorInstall={params.row} />,
    },
]

interface ActionButtonProps {
    sensorInstall: SensorInstallInfo
}

const SensorInstallActions = memo((props: ActionButtonProps) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <Box>
            <IconButton
                aria-label="more"
                id="sensor-install-row-menu-button"
                aria-controls={open ? "menu-button" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup="true"
                onClick={handleClick}
            >
                <MoreVert />
            </IconButton>
            <Menu
                id="sensor-install-row-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                sx={{
                    "& .MuiMenuItem-root": {
                        "& .MuiSvgIcon-root": {
                            color: theme.palette.text.secondary,
                            marginRight: 2,
                        },
                    },
                }}
            >
                <EditSensorInstallMenuItem sensorInstall={props.sensorInstall} closeMenu={handleClose} />
                <DownloadBoresightFileMenuItem sensorInstall={props.sensorInstall} closeMenu={handleClose} />
                <DeleteSensorInstallMenuItem sensorInstall={props.sensorInstall} closeMenu={handleClose} />
            </Menu>
        </Box>
    )
})

interface MenuItemProps {
    sensorInstall: SensorInstallInfo
    closeMenu: () => void
}

const DownloadBoresightFileMenuItem = (props: MenuItemProps) => {
    const handleDownloadFile = (configFileInfo: SensorInstallInfo) => {
        // the database saves the file data as a string instead of the file itself
        const blob = new Blob([configFileInfo.file_data], { type: configFileInfo.file_format })

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = configFileInfo.file_name
        document.body.appendChild(a) // required for firefox
        a.click()
        a.remove()
    }

    return (
        <MenuItem
            onClick={() => {
                handleDownloadFile(props.sensorInstall)
                props.closeMenu()
            }}
            disableRipple={true}
        >
            <InsertDriveFile /> Download File
        </MenuItem>
    )
}
